import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent, Stack } from "@mui/material";
import { type ReviewsViewContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { Card } from "@src/appV2/redesign/system/ui-components";
import {
  type ReviewSource,
  type WorkplaceCommentAttributesType,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { parseISO } from "date-fns";

import { type CommentReactionSource, type Reaction } from "../types";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import {
  CompactCardLockedContent,
  type CompactCardLockedContentProps,
} from "./CompactCardLockedContent";
import { ReviewContextPills } from "./ReviewContextPills";
import { VideoCommentPlayer } from "./VideoCommentPlayer";

interface WorkplaceReviewCommentCompactCardProps {
  commentId: string;
  commentText: string;
  commentType?: "WRITTEN" | "VIDEO";
  mediaUrl?: string;
  createdAtIso: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  totalLikes: number;
  workerName: string;
  totalDislikes: number;
  workerReaction?: Reaction;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  workplaceId: string;
  maxLines?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  reviewTag?: WorkplaceCommentAttributesType["reviewTag"];
  shiftWorkerQualification?: string;
  rating?: number;
  useDelayedReviewLayout?: boolean;
  showEngagementActions?: boolean;
  isReadOnly?: boolean;
  isLocked?: boolean;
  onUnlockPress?: () => void;
  reactionSource: CommentReactionSource;
  viewContext: ReviewsViewContext;
  source?: ReviewSource;
  reviewUrl?: string;
}

export function WorkplaceReviewCommentCompactCard(props: WorkplaceReviewCommentCompactCardProps) {
  const { onClick, isReadOnly = false, commentType, mediaUrl, isLocked = false } = props;
  const hasInlineVideo = commentType === "VIDEO" && isDefined(mediaUrl);

  return (
    <Card outlined variant="tertiary">
      {isReadOnly || hasInlineVideo ? (
        <WorkplaceReviewCommentCompactCardContent {...props} />
      ) : (
        <CardActionArea
          sx={{ height: "100%", minHeight: "11rem" }}
          onClick={isLocked ? undefined : onClick}
        >
          <WorkplaceReviewCommentCompactCardContent {...props} />
        </CardActionArea>
      )}
    </Card>
  );
}

type WorkplaceReviewCommentCompactCardContentProps = Omit<
  WorkplaceReviewCommentCompactCardProps,
  "onClick" | "isReadOnly"
>;

function WorkplaceReviewCommentCompactCardContent(
  props: WorkplaceReviewCommentCompactCardContentProps
) {
  const {
    commentId,
    commentText,
    commentType,
    mediaUrl,
    createdAtIso,
    onLikeChanged,
    totalLikes,
    workerName,
    totalDislikes,
    workerReaction,
    profilePictureUrl,
    isLoadingProfilePicture,
    workplaceId,
    maxLines,
    shiftPeriod,
    patientsPerNurse,
    reviewTag,
    shiftWorkerQualification,
    rating,
    useDelayedReviewLayout,
    showEngagementActions = true,
    isLocked = false,
    onUnlockPress,
    reactionSource,
    viewContext,
    source,
    reviewUrl,
  } = props;

  const isVideoComment = commentType === "VIDEO" && isDefined(mediaUrl);

  const lockedContentProps: CompactCardLockedContentProps = {
    commentText,
    maxLines,
    showEngagementActions,
    workerReaction,
    totalLikes,
    totalDislikes,
    workplaceId,
    commentId,
    reactionSource,
    viewContext,
    isLocked,
    onLikeChanged,
    onUnlockPress,
  };

  return (
    <CardContent
      sx={{
        padding: 7,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
      }}
    >
      {useDelayedReviewLayout ? (
        <>
          <Stack spacing={4}>
            <WorkplaceReviewCommentAuthorInfo
              useDelayedReviewLayout
              workerName={workerName}
              profilePictureUrl={profilePictureUrl}
              isLoadingProfilePicture={isLoadingProfilePicture}
              createdAt={parseISO(createdAtIso)}
              shiftWorkerQualification={shiftWorkerQualification}
              reviewUrl={reviewUrl}
            />
            <ReviewContextPills
              shiftPeriod={shiftPeriod}
              patientsPerNurse={patientsPerNurse}
              reviewTag={reviewTag}
              rating={rating}
              source={source}
            />
            {isVideoComment && (
              <VideoCommentPlayer url={mediaUrl} workplaceId={workplaceId} commentId={commentId} />
            )}
          </Stack>
          <CompactCardLockedContent {...lockedContentProps} />
        </>
      ) : (
        <Stack spacing={4}>
          {isVideoComment && (
            <VideoCommentPlayer url={mediaUrl} workplaceId={workplaceId} commentId={commentId} />
          )}
          <WorkplaceReviewCommentAuthorInfo
            workerName={workerName}
            profilePictureUrl={profilePictureUrl}
            isLoadingProfilePicture={isLoadingProfilePicture}
            createdAt={parseISO(createdAtIso)}
            reviewUrl={reviewUrl}
          />
          <CompactCardLockedContent {...lockedContentProps} />
        </Stack>
      )}
    </CardContent>
  );
}
